import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface DashboardStats {
  totalMembers: number;
  activeMembers: number;
  paidMembers: number;
  unpaidMembers: number;
  collectionRate: number;
  totalCollected: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [memberRes, duesRes] = await Promise.all([
          api.get('/reports/members/count'),
          api.get('/reports/memberships/dues-summary'),
        ]);

        const members = memberRes.data.data;
        const dues = duesRes.data.data;

        const totalMembers = members.reduce(
          (sum: number, d: any) => sum + Number(d.total_members), 0
        );
        const activeMembers = members.reduce(
          (sum: number, d: any) => sum + Number(d.active_members), 0
        );
        const paidMembers = dues.reduce(
          (sum: number, d: any) => sum + Number(d.paid_count), 0
        );
        const unpaidMembers = dues.reduce(
          (sum: number, d: any) => sum + Number(d.unpaid_count), 0
        );
        const totalCollected = dues.reduce(
          (sum: number, d: any) => sum + Number(d.total_collected), 0
        );
        const collectionRate = totalMembers > 0
          ? Math.round((paidMembers / totalMembers) * 100)
          : 0;

        setStats({
          totalMembers,
          activeMembers,
          paidMembers,
          unpaidMembers,
          collectionRate,
          totalCollected,
        });
      } catch (err: any) {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {greeting()}, {user?.otherNames} 👋
          </h1>
          <p style={styles.subtitle}>
            Here is your NASK association overview
          </p>
        </div>
        <div style={styles.dateBadge}>
          {new Date().toLocaleDateString('en-GB', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      {/* Stats Cards */}
      {loading ? (
        <div style={styles.loadingBox}>Loading dashboard data...</div>
      ) : stats && (
        <div style={styles.statsGrid}>
          <StatCard
            icon="👥"
            label="Total Members"
            value={stats.totalMembers.toString()}
            sub={stats.activeMembers + ' active'}
            color="#3b82f6"
          />
          <StatCard
            icon="✅"
            label="Dues Paid"
            value={stats.paidMembers.toString()}
            sub={stats.collectionRate + '% collection rate'}
            color="#22c55e"
          />
          <StatCard
            icon="⚠️"
            label="Dues Unpaid"
            value={stats.unpaidMembers.toString()}
            sub="Members not current"
            color="#f59e0b"
          />
          <StatCard
            icon="₦"
            label="Total Collected"
            value={'₦' + stats.totalCollected.toLocaleString()}
            sub="Successful payments"
            color="#cc0000"
          />
        </div>
      )}

      {/* Quick Links */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Quick Reports</h2>
        <div style={styles.quickLinks}>
          <QuickLink
            icon="👥"
            label="Member Census"
            desc="Full member list by dojo and rank"
            onClick={() => navigate('/reports/members')}
          />
          <QuickLink
            icon="💳"
            label="Dues Status"
            desc="Who has paid and who hasn't"
            onClick={() => navigate('/reports/memberships')}
          />
          <QuickLink
            icon="🥋"
            label="Grading Results"
            desc="Results by event and candidate"
            onClick={() => navigate('/reports/gradings')}
          />
          <QuickLink
            icon="✅"
            label="Eligibility"
            desc="Who is ready to grade"
            onClick={() => navigate('/reports/eligibility')}
          />
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  icon: string;
  label: string;
  value: string;
  sub: string;
  color: string;
}> = ({ icon, label, value, sub, color }) => (
  <div style={cardStyles.card}>
    <div style={{ ...cardStyles.iconBox, backgroundColor: color + '22' }}>
      <span style={{ fontSize: '24px' }}>{icon}</span>
    </div>
    <div style={cardStyles.content}>
      <p style={cardStyles.label}>{label}</p>
      <p style={{ ...cardStyles.value, color }}>{value}</p>
      <p style={cardStyles.sub}>{sub}</p>
    </div>
  </div>
);

const QuickLink: React.FC<{
  icon: string;
  label: string;
  desc: string;
  onClick: () => void;
}> = ({ icon, label, desc, onClick }) => (
  <div style={quickStyles.card} onClick={onClick}>
    <span style={quickStyles.icon}>{icon}</span>
    <div>
      <p style={quickStyles.label}>{label}</p>
      <p style={quickStyles.desc}>{desc}</p>
    </div>
  </div>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '32px',
    fontFamily: "'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
  },
  title: {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#888888',
    fontSize: '14px',
    margin: '0',
  },
  dateBadge: {
    color: '#666666',
    fontSize: '13px',
    backgroundColor: '#1a1a1a',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #2a2a2a',
  },
  errorBox: {
    backgroundColor: '#2d1a1a',
    border: '1px solid #cc3333',
    color: '#ff6666',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  loadingBox: {
    color: '#888888',
    textAlign: 'center',
    padding: '48px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '40px',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
  },
  quickLinks: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
  },
};

const cardStyles: { [key: string]: React.CSSProperties } = {
  card: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  iconBox: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  label: {
    color: '#888888',
    fontSize: '12px',
    margin: '0',
    fontWeight: '500',
  },
  value: {
    fontSize: '24px',
    fontWeight: '700',
    margin: '4px 0',
  },
  sub: {
    color: '#666666',
    fontSize: '11px',
    margin: '0',
  },
};

const quickStyles: { [key: string]: React.CSSProperties } = {
  card: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  icon: {
    fontSize: '24px',
  },
  label: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    margin: '0 0 2px 0',
  },
  desc: {
    color: '#666666',
    fontSize: '12px',
    margin: '0',
  },
};

export default Dashboard;